import { apiClient } from "../client";
import { ApiErrorHandler } from "../utils/error-handler";
import { Document } from "../../../types/document";
import { PaginatedResponse } from "../types/common";

/**
 * Endpoints de documentos
 * 
 * Funcionalidades:
 * - CRUD de documentos
 * - Upload e download
 * - Busca por processo
 * - Gerenciamento de tipos
 */

export interface CreateDocumentRequest {
  name: string;
  description: string;
  document_type: string | number;
  process: string | number;
}

export interface UpdateDocumentRequest {
  id: string;
  data: Partial<Document>;
}

export const documentApi = {
  /**
   * Lista todos os documentos.
   *
   * Use este método para buscar a lista de documentos.
   * - NÃO use documentApi.get('/documents/', ...), pois get espera um ID e não um endpoint.
   * - O retorno pode ser um array direto ou um objeto paginado (com .results).
   * - O token JWT é adicionado automaticamente pelo interceptor do Axios.
   *
   * Exemplo de uso seguro:
   *   const response = await documentApi.list();
   *   const docs = Array.isArray(response) ? response : response.results || [];
   */
  list: async (): Promise<PaginatedResponse<Document>> => {
    try {
      const response = await apiClient.get("/documents/");
      return response.data;
    } catch (error) {
      ApiErrorHandler.handleRequestError(error, "DocumentAPI.list");
    }
  },

  /**
   * Busca um documento pelo ID.
   *
   * Use este método apenas para buscar um documento específico.
   * - NÃO passe endpoint ou string '/documents/' como parâmetro, apenas o ID do documento.
   * - Exemplo correto: documentApi.get('123')
   * - Exemplo errado: documentApi.get('/documents/', ...)
   *
   * O token JWT é adicionado automaticamente pelo interceptor do Axios.
   */
  get: async (id: string): Promise<Document> => {
    try {
      const response = await apiClient.get(`/documents/${id}/`);
      return response.data;
    } catch (error) {
      ApiErrorHandler.handleRequestError(error, "DocumentAPI.get");
    }
  },

  create: async (data: CreateDocumentRequest): Promise<Document> => {
    try {
      const response = await apiClient.post("/documents/", data);
      return response.data;
    } catch (error) {
      ApiErrorHandler.handleRequestError(error, "DocumentAPI.create");
    }
  },

  update: async (id: string, data: Partial<Document>): Promise<Document> => {
    try {
      const response = await apiClient.patch(`/documents/${id}/`, data);
      return response.data;
    } catch (error) {
      ApiErrorHandler.handleRequestError(error, "DocumentAPI.update");
    }
  },

  delete: async (id: string): Promise<void> => {
    try {
      await apiClient.delete(`/documents/${id}/`);
    } catch (error) {
      ApiErrorHandler.handleRequestError(error, "DocumentAPI.delete");
    }
  },

  getByProcess: async (processId: string): Promise<PaginatedResponse<Document>> => {
    try {
      const response = await apiClient.get(`/documents/?process=${processId}`);
      return response.data;
    } catch (error) {
      ApiErrorHandler.handleRequestError(error, "DocumentAPI.getByProcess");
    }
  },

  download: async (id: string): Promise<Blob> => {
    try {
      const response = await apiClient.get(`/documents/${id}/download/`, {
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      ApiErrorHandler.handleRequestError(error, "DocumentAPI.download");
    }
  },
};

/**
 * Endpoints de tipos de documento
 */
export const documentTypeApi = {
  /**
   * Lista todos os tipos de documento
   */
  list: () => apiClient.get("/document-types/"),

  /**
   * Busca um tipo de documento pelo ID
   * 
   * @param id - ID do tipo de documento
   */
  get: (id: string) => apiClient.get(`/document-types/${id}/`),
}; 